import React, { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import type Keycloak from 'keycloak-js';
import type { KeycloakInitOptions } from 'keycloak-js';
import { KeycloakContext } from './auth-context';

/**
 * keycloak-js flips `instance.authenticated` to `false` synchronously when
 * `init()` starts, so `authenticated !== undefined` cannot distinguish
 * "initialized" from "init in flight". React StrictMode (dev) invokes the
 * mount effect twice a millisecond apart; without this registry the second
 * invocation misreads the in-flight instance as initialized-unauthenticated
 * and mounts children with no roles (the principal-gate flash). The in-flight
 * init is tracked per singleton instance and every effect run awaits the same
 * promise.
 */
const initPromises = new WeakMap<Keycloak, Promise<boolean>>();

/**
 * Props for AuthProvider component
 */
export interface AuthProviderProps extends PropsWithChildren {
  /** Keycloak instance to use for authentication */
  instance: Keycloak;
  /** How to handle initial load */
  onLoad?: 'login-required' | 'check-sso';
  /** Custom initialization options */
  initOptions?: KeycloakInitOptions;
  /** Component to show while Keycloak is initializing */
  loadingFallback?: React.ReactNode;
  /** Callback fired when initialization completes */
  onInitialized?: (authenticated: boolean) => void;
  /** Callback fired when initialization fails */
  onInitError?: (error: Error) => void;
}

/**
 * Provider component that initializes Keycloak and provides authentication context.
 * Simplified version to avoid React Strict Mode issues.
 */
export function AuthProvider(props: AuthProviderProps) {
  const {
    instance,
    children,
    initOptions,
    onLoad = 'check-sso',
    loadingFallback = null,
    onInitialized,
    onInitError,
  } = props;

  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);
  const [tokenVersion, setTokenVersion] = useState(0);

  /**
   * Force a token refresh from Keycloak and trigger a re-render.
   * This is useful when user attributes in Keycloak have changed
   * and we need a new token with updated claims.
   */
  const forceTokenRefresh = useCallback(async (): Promise<boolean> => {
    if (!instance) {
      return false;
    }

    try {
      // Pass a very large minValidity to force refresh regardless of current token validity
      // This works because updateToken refreshes if token expires within minValidity seconds
      const refreshed = await instance.updateToken(Number.MAX_SAFE_INTEGER);
      
      if (refreshed) {
        // Increment token version to trigger re-renders in consuming components
        setTokenVersion((v) => v + 1);
      }
      
      return refreshed;
    } catch (error) {
      console.error('Failed to force token refresh:', error);
      return false;
    }
  }, [instance]);

  // Stable reference for context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ keycloak: instance, initialized, authenticated, tokenVersion, forceTokenRefresh }),
    [instance, initialized, authenticated, tokenVersion, forceTokenRefresh]
  );

  useEffect(() => {
    // Skip if already initialized or if there's an error
    if (initialized || initError) {
      return;
    }

    let cancelled = false;
    const applyInitialized = (isAuthenticated: boolean): void => {
      if (cancelled) return;
      setAuthenticated(isAuthenticated);
      setInitialized(true);
      onInitialized?.(isAuthenticated);
    };
    const applyInitError = (error: unknown): void => {
      if (cancelled) return;
      const resolvedError =
        error instanceof Error
          ? error
          : new Error('Keycloak initialization failed');
      setInitError(resolvedError);
      setAuthenticated(false);
      setInitialized(true);
      onInitError?.(resolvedError);
    };

    // An init started by an earlier effect run (StrictMode remount, Fast
    // Refresh) or already settled: adopt its outcome instead of re-initializing.
    const inFlight = initPromises.get(instance);
    if (inFlight) {
      inFlight.then(applyInitialized, applyInitError);
      return () => {
        cancelled = true;
      };
    }

    // Instance initialized outside this provider (no registry entry): adopt.
    if (instance.authenticated !== undefined) {
      applyInitialized(Boolean(instance.authenticated));
      return;
    }

    const defaultInitOptions: KeycloakInitOptions = {
      onLoad,
      checkLoginIframe: false,
      pkceMethod: 'S256',
      ...initOptions,
    };

    let initPromise: Promise<boolean>;
    try {
      initPromise = instance.init(defaultInitOptions).then(Boolean);
    } catch (error) {
      applyInitError(error);
      return;
    }
    initPromises.set(instance, initPromise);
    initPromise.then(applyInitialized, applyInitError);
    return () => {
      cancelled = true;
    };
  }, [instance, initialized, initError, initOptions, onLoad, onInitialized, onInitError]);

  // Show loading state while initializing
  if (!initialized) {
    return <>{loadingFallback}</>;
  }

  // Surface initialization failures to the nearest error boundary
  // (e.g. AuthErrorBoundary) so a Keycloak outage degrades cleanly.
  if (initError) {
    throw initError;
  }

  return (
    <KeycloakContext.Provider value={contextValue}>
      {children}
    </KeycloakContext.Provider>
  );
}

export default AuthProvider;